import { Component } from '@theme/component';
import { syncCartOffer } from '@theme/cart-offer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/** Coordinates page restoration, external cart changes and standard checkout. */
class CartOfferComponent extends Component {
  #controller;
  #checkingOut = false;
  #pendingMutations = new Set();
  #drawerSync;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onMutation, { signal });
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen, { signal });
    document.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.#sync().catch(this.#showError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#pendingMutations.clear();
  }

  #showError = () => {
    if (this.isConnected) this.textContent = Theme.translations.cart_offer_error;
  };

  #onPageShow = (event) => {
    if (event.persisted) this.#sync().catch(this.#showError);
  };

  /** Recheck restored carts and additions made outside the theme's product form. */
  #onDrawerOpen = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== 'cart-drawer' || this.#drawerSync) return;
    this.#drawerSync = this.#sync().catch(this.#showError).finally(() => { this.#drawerSync = undefined; });
  };

  #onMutation = (event) => {
    if (event.target === this || !event.promise) return;
    const operation = event.promise.then(async ({ detail }) => {
      if (this.#controller.signal.aborted) return;
      // Theme producers already reconcile before resolving their event promise.
      if (['product-form-component', 'cart-items-component'].includes(detail?.source)) return;
      await this.#sync();
    });
    this.#pendingMutations.add(operation);
    operation.catch(this.#showError).finally(() => this.#pendingMutations.delete(operation));
  };

  async #sync() {
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId).filter(Boolean))];
    const cart = await syncCartOffer(sections);
    if (!this.isConnected || !cart.offerChanged) return cart;
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update', context: 'cart',
      lines: cart.offerLines,
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: cart.sections, source: 'cart-offer', didError: false },
      }),
    }));
    return cart;
  }

  /** Wait for in-flight adds/removals before native cart checkout submission. */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form' ||
      !(event.submitter instanceof HTMLButtonElement) || event.submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#checkingOut) return;
    this.#checkingOut = true;
    this.textContent = '';
    try {
      await Promise.allSettled([...this.#pendingMutations]);
      const cart = await this.#sync();
      if (!cart?.items.length) return;
      if (!this.isConnected) return;
      const currentForm = document.getElementById('cart-form');
      if (!(currentForm instanceof HTMLFormElement)) throw new Error('Cart form missing');
      // Native submit bypasses this handler; retain Shopify's checkout submit value.
      const checkout = document.createElement('input');
      checkout.type = 'hidden';
      checkout.name = 'checkout';
      checkout.value = 'Checkout';
      currentForm.append(checkout);
      currentForm.submit();
    } catch {
      this.#showError();
    } finally {
      this.#checkingOut = false;
    }
  };
}

if (!customElements.get('cart-offer-component')) {
  customElements.define('cart-offer-component', CartOfferComponent);
}
